import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

const empty = {
  title: "", offer_type: "percent", discount_value: 10, max_discount_cap: 150,
  code: "", auto_apply: false, min_order_value: 0, status: "active",
  start_at: null, end_at: null, days_of_week: [], usage_limit: null, one_per_customer: false,
};

export default function OffersManager() {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("active");

  const load = () => api.get("/restaurant/offers").then((r) => setOffers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...o }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/restaurant/offers/${editing.id}`, form);
      else await api.post("/restaurant/offers", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const setStatus = async (o, status) => {
    await api.patch(`/restaurant/offers/${o.id}`, { status });
    load();
  };
  const del = async (o) => {
    if (!confirm("Delete offer?")) return;
    await api.delete(`/restaurant/offers/${o.id}`);
    load();
  };

  const filtered = offers.filter((o) => tab === "all" || o.status === tab);

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-dark">Offers</h1>
            <div className="text-sm text-brand-dark/60">Run promotions your customers will love.</div>
          </div>
          <Button onClick={openNew} className="bg-brand-orange text-white" data-testid="new-offer">
            <Plus size={14} className="mr-1" /> New Offer
          </Button>
        </div>
        <div className="flex gap-2 mb-3 text-sm">
          {["active", "scheduled", "expired", "draft", "all"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`otab-${k}`}
              className={`px-3 py-1.5 rounded-full capitalize ${tab === k ? "bg-brand-dark text-brand-light" : "bg-white text-brand-dark/70 border border-brand-dark/10"}`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {filtered.length === 0 && <div className="text-brand-dark/60 py-10 text-center text-sm">No offers.</div>}
          {filtered.map((o) => (
            <div key={o.id} className="bg-white rounded-xl border border-brand-dark/5 p-4" data-testid={`offer-row-${o.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-semibold text-brand-dark">{o.title}</div>
                  <div className="text-xs text-brand-dark/60 mt-0.5">
                    {o.offer_type === "percent" ? `${o.discount_value}%` : inr(o.discount_value)} off
                    {o.min_order_value ? ` · Min ${inr(o.min_order_value)}` : ""}
                    {o.code ? ` · Code: ${o.code}` : " · Auto-apply"}
                  </div>
                  <div className="text-xs text-brand-dark/50 mt-0.5">
                    Used {o.times_used}× · Viewed {o.times_viewed}×
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={o.status} onChange={(e) => setStatus(o, e.target.value)} className="text-xs border border-input rounded px-2 py-1">
                    <option value="active">active</option>
                    <option value="paused">paused</option>
                    <option value="scheduled">scheduled</option>
                    <option value="expired">expired</option>
                    <option value="draft">draft</option>
                  </select>
                  <button onClick={() => openEdit(o)} className="text-xs text-brand-dark/70 hover:text-brand-orange">Edit</button>
                  <button onClick={() => del(o)} className="text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit offer" : "New offer"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-title" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.offer_type} onChange={(e) => setForm({ ...form, offer_type: e.target.value })} className="h-10 rounded border border-input px-2">
                <option value="percent">Percent off</option>
                <option value="flat">Flat ₹ off</option>
                <option value="bogo">BOGO</option>
                <option value="free_item">Free item on min order</option>
              </select>
              <Input type="number" placeholder="Value" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: +e.target.value })} data-testid="offer-value" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Min order value" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: +e.target.value })} />
              <Input type="number" placeholder="Max cap (percent)" value={form.max_discount_cap || ""} onChange={(e) => setForm({ ...form, max_discount_cap: +e.target.value || null })} />
            </div>
            <div className="grid grid-cols-2 gap-2 items-center">
              <Input placeholder="Code (leave empty for auto-apply)" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="offer-code" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.auto_apply} onChange={(e) => setForm({ ...form, auto_apply: e.target.checked })} /> Auto-apply
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.one_per_customer} onChange={(e) => setForm({ ...form, one_per_customer: e.target.checked })} /> One redemption per customer
            </label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 rounded border border-input px-2">
              <option value="active">active</option>
              <option value="scheduled">scheduled</option>
              <option value="draft">draft</option>
            </select>
            <Button onClick={save} className="w-full bg-brand-orange text-white" data-testid="save-offer">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </RestaurantLayout>
  );
}
